import { useNavigate } from 'react-router';

import { Button } from '@llura/ui';

import { usePushSubscription } from '../../hooks/usePushSubscription';
import { useNotificationPrefsStore } from '../../stores/notification-prefs.store';
import { useStrings } from '../../hooks/useStrings';
import { isIos, isStandalone } from '../../lib/platform';

// iOS only delivers web push from a PWA installed to the Home Screen (16.4+).
// In a normal Safari tab the subscribe button can't work, so we show an
// install hint and don't pretend otherwise.
export function PushOptInPage() {
  const navigate = useNavigate();
  const { strings } = useStrings();
  const t = strings.notifications.optIn;

  const { status, subscribe } = usePushSubscription();
  const setReminderEnabled = useNotificationPrefsStore((s) => s.setReminderEnabled);
  const markOptInSeen = useNotificationPrefsStore((s) => s.markOptInSeen);

  const iosNotInstalled = isIos() && !isStandalone();

  const proceed = () => {
    markOptInSeen();
    navigate('/dashboard', { replace: true });
  };

  const enable = async () => {
    await subscribe();
    // Permission may be denied inside subscribe(); only flip the pref on when
    // we actually reached a subscribed state.
    if (useNotificationPrefsStore.getState().reminderEnabled === false) {
      setReminderEnabled(true);
    }
    proceed();
  };

  const hint =
    status === 'denied'
      ? t.deniedHint
      : status === 'unsupported'
        ? t.unsupportedHint
        : iosNotInstalled
          ? t.iosInstallHint
          : null;

  const canEnable = status !== 'unsupported' && status !== 'denied' && !iosNotInstalled;

  return (
    <div className="relative min-h-[100svh] overflow-hidden bg-background">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 right-[-15%] h-[420px] w-[420px] rounded-full bg-primary/10 blur-3xl"
      />

      <header className="sticky top-0 z-10 flex items-center gap-2 bg-background/80 px-4 py-3 backdrop-blur">
        <span className="font-serif text-lg">{strings.brand.name}</span>
      </header>

      <main className="relative mx-auto w-full max-w-md px-5 pb-16 pt-12">
        <h1 className="font-serif text-3xl">{t.title}</h1>
        <p className="mt-3 text-sm text-muted-foreground">{t.body}</p>

        {hint && (
          <p className="mt-6 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">{hint}</p>
        )}

        {iosNotInstalled && (
          <Button
            variant="link"
            className="mt-2 h-auto px-0 text-sm text-primary"
            onClick={() => navigate('/profile/install')}
          >
            {strings.notifications.optIn.installGuideCta}
          </Button>
        )}

        <div className="mt-8 space-y-3">
          {canEnable && (
            <Button
              className="w-full"
              onClick={enable}
              disabled={status === 'subscribing'}
            >
              {t.enableCta}
            </Button>
          )}
          <Button variant="ghost" className="w-full text-muted-foreground" onClick={proceed}>
            {t.laterCta}
          </Button>
        </div>
      </main>
    </div>
  );
}
